import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Row, Col, Card, Pagination, Empty, Spin, Tag, Space, Typography, Statistic, Alert, Button, Checkbox } from 'antd';
import { CalendarOutlined, FileTextOutlined, DollarOutlined, RightOutlined, BankOutlined, UserOutlined, QuestionCircleOutlined, LineChartOutlined, InboxOutlined } from '@ant-design/icons';
import CurrencyFormat from 'react-currency-format';
import lib from '../../../assets/utils/lib';
import { notification } from 'antd';
import dayjs from 'dayjs';
import { useSearchParams } from 'react-router-dom';
import BatchLoadingSkeleton from '../Batches/BatchLoadingSkeleton';
import ArchiveClaimsModal from '../ArchiveClaimsModal';
import { canManageClaimsArchive } from '../claimsArchiveHelpers';
import { useAuth } from '../../../assets/utils/useAuth';
import SearchInput from '../SearchInput';
import {
  getClaimsArchiveScope,
  withClaimsArchiveScope,
} from '../claimsArchiveHelpers';
import {
  getClaimsApprovedTotal,
  getPaApprovedTotal,
  getSubmittedTotal,
} from '../claimFinancialHelpers';
import './AdminClaimsMonthsList.css';

const { Text, Title } = Typography;

/**
 * AdminClaimsMonthsList - Displays all months with claims (Admin)
 * First level of the simplified 3-level hierarchy
 */
const AdminClaimsMonthsList = ({ onSelect }) => {
  const [searchParams] = useSearchParams();
  const MONTHS_SEARCH_PARAM = 'adminMonthsSearch';
  const MONTHS_PAGE_PARAM = 'adminMonthsPage';
  const MONTHS_PAGE_SIZE_PARAM = 'adminMonthsPageSize';
  const [months, setMonths] = useState([]);
  const [aggregates, setAggregates] = useState(null);
  const [searchMeta, setSearchMeta] = useState(null);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 12, // 12 months = 1 year
    total: 0,
  });
  const fetchingRef = useRef(false);
  // Archive by month, or several months at once, without opening each facility.
  const { user } = useAuth();
  const canArchiveClaims = canManageClaimsArchive(user);
  const archivedView = getClaimsArchiveScope(searchParams) === 'archived';
  const [selectedMonths, setSelectedMonths] = useState([]);
  const [archiveRequest, setArchiveRequest] = useState(null);
  const monthKeyOf = (m) => m.month || m.month_key;
  const toggleMonth = (key, checked) => setSelectedMonths((prev) => (checked ? [...new Set([...prev, key])] : prev.filter((k) => k !== key)));
  const openArchiveFor = (keys) => {
    const list = Array.isArray(keys) ? keys : [keys];
    if (!list.length) return;
    setArchiveRequest(list.length === 1 ? { scope: 'month', month: list[0] } : { scope: 'months', months: list });
  };
  
  // Get search query from URL
  const searchQuery = searchParams.get(MONTHS_SEARCH_PARAM) || '';

  const fetchMonths = useCallback(async (page = 1, search = '') => {
    // Prevent duplicate calls
    if (fetchingRef.current) return;
    fetchingRef.current = true;

    setLoading(true);
    try {
      const params = withClaimsArchiveScope({
        page,
        pageSize: pagination.pageSize,
      }, getClaimsArchiveScope(searchParams));
      
      // Add search parameter if provided
      if (search && search.trim()) {
        params.search = search.trim();
      }
      
      const response = await lib.getAdminClaimsMonths(params);

      if (response?.data?.error) {
        notification.error({
          message: 'Error',
          description: response.data.message || 'Failed to fetch months',
        });
        return;
      }

      // Backend returns: { error, message, data: [...months...], aggregates: {...}, pagination: {...}, search: {...} }
      const responseData = response?.data || {};
      const monthsList = Array.isArray(responseData.data) ? responseData.data : [];
      
      setMonths(monthsList);
      setAggregates(responseData.aggregates || null);
      setSearchMeta(responseData.search || null); // Store search metadata
      setPagination((prev) => ({
        ...prev,
        current: responseData.pagination?.page || page,
        pageSize: responseData.pagination?.pageSize || prev.pageSize,
        total: responseData.pagination?.total || monthsList.length || 0,
      }));
    } catch (error) {
      console.error('Error fetching months:', error);
      notification.error({
        message: 'Error',
        description: error?.response?.data?.message || 'Failed to fetch months',
      });
    } finally {
      setLoading(false);
      fetchingRef.current = false;
    }
  }, [pagination.pageSize, searchParams]); // eslint-disable-line react-hooks/exhaustive-deps -- searchQuery passed as arg, not from closure

  useEffect(() => {
    // Get current page from URL or state
    const page = parseInt(searchParams.get(MONTHS_PAGE_PARAM) || pagination.current.toString());
    // Always use the current searchQuery from URL
    const currentSearch = searchParams.get(MONTHS_SEARCH_PARAM) || '';
    fetchMonths(page, currentSearch);
  }, [pagination, fetchMonths, searchParams]); // eslint-disable-line react-hooks/exhaustive-deps

  const handlePageChange = (page, pageSize) => {
    setPagination((prev) => ({
      ...prev,
      current: page,
      pageSize: pageSize || prev.pageSize,
    }));
    // Update URL with new page
    const newParams = new URLSearchParams(searchParams);
    newParams.set(MONTHS_PAGE_PARAM, page.toString());
    if (pageSize) {
      newParams.set(MONTHS_PAGE_SIZE_PARAM, pageSize.toString());
    }
    window.history.replaceState({}, '', `${window.location.pathname}?${newParams.toString()}`);
  };

  const handleSearch = (query) => {
    // Search is handled via URL params in SearchInput component
    // Just trigger a refetch
    setPagination(prev => ({ ...prev, current: 1 }));
  };

  const formatMonthLabel = (monthString) => {
    const date = dayjs(monthString + '-01');
    return date.format('MMMM YYYY');
  };

  return (
    <div className="admin-claims-months-list">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px', flexWrap: 'wrap', gap: '16px' }}>
        <Title level={2} style={{ margin: 0 }}>
          All Claims by Month
        </Title>
        <Space>
          <SearchInput 
            placeholder="Search months (e.g., '2024', 'January', '2024-01')" 
            onSearch={handleSearch}
            loading={loading}
            searchParamName={MONTHS_SEARCH_PARAM}
            pageParamName={MONTHS_PAGE_PARAM}
          />
          {canArchiveClaims ? (
            <Button
              icon={<InboxOutlined />}
              danger={!archivedView}
              disabled={!selectedMonths.length}
              onClick={() => openArchiveFor(selectedMonths)}
            >
              {archivedView ? 'Restore selected' : 'Archive selected'}{selectedMonths.length ? ` (${selectedMonths.length})` : ''}
            </Button>
          ) : null}
        </Space>
      </div>

      {loading && <BatchLoadingSkeleton />}

      {/* Search Results Info */}
      {searchMeta && searchMeta.applied && (
        <Alert
          message={
            <Space>
              <span>Found <strong>{searchMeta.results_count || 0}</strong> result{searchMeta.results_count !== 1 ? 's' : ''} for</span>
              <Tag color="blue">"{searchMeta.query}"</Tag>
            </Space>
          }
          type="info"
          showIcon
          closable
          style={{ marginBottom: '24px' }}
          onClose={() => {
            const newParams = new URLSearchParams(searchParams);
            newParams.delete(MONTHS_SEARCH_PARAM);
            newParams.set(MONTHS_PAGE_PARAM, '1');
            window.history.replaceState({}, '', `${window.location.pathname}?${newParams.toString()}`);
            handleSearch('');
          }}
        />
      )}

      {months.length === 0 && !loading && (
        <Empty
          description={searchQuery ? `No months found for "${searchQuery}"` : "No months with claims found"}
          style={{ padding: '40px' }}
        />
      )}
      
      {/* Dashboard Aggregates Cards */}
      {aggregates && (
        <Row gutter={[16, 16]} style={{ marginBottom: '32px' }}>
          <Col xs={24} sm={12} md={8} lg={6}>
            <Card
              style={{
                height: '100%',
                background: 'linear-gradient(135deg, #e6f4ff 0%, #bae0ff 100%)',
                border: '2px solid #1890ff',
                borderRadius: '16px',
                boxShadow: '0 4px 16px rgba(24, 144, 255, 0.15)',
                overflow: 'hidden',
                position: 'relative',
              }}
              bodyStyle={{ padding: '20px' }}
            >
              <div style={{ position: 'absolute', top: 0, right: 0, width: '100px', height: '100px', background: 'radial-gradient(circle, rgba(24, 144, 255, 0.2) 0%, transparent 70%)', borderRadius: '50%', transform: 'translate(30%, -30%)', pointerEvents: 'none' }} />
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', position: 'relative', zIndex: 1 }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <Text style={{ fontSize: '12px', fontWeight: 500, color: '#262626', display: 'block', marginBottom: '8px' }}>Total Enrollees</Text>
                  <Text style={{ fontSize: '26px', fontWeight: 600, color: '#0050b3', display: 'block', marginBottom: '4px' }}>{aggregates.total_unique_enrollees || 0}</Text>
                  <div style={{ height: '1px', background: 'rgba(24, 144, 255, 0.3)', marginTop: '8px' }} />
                </div>
                <div style={{ width: '48px', height: '48px', borderRadius: '12px', background: 'rgba(24, 144, 255, 0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '24px', color: '#1890ff', flexShrink: 0 }}>
                  <UserOutlined />
                </div>
              </div>
            </Card>
          </Col>
          <Col xs={24} sm={12} md={8} lg={6}>
            <Card
              style={{
                height: '100%',
                background: 'linear-gradient(135deg, #f6ffed 0%, #d9f7be 100%)',
                border: '2px solid #52c41a',
                borderRadius: '16px',
                boxShadow: '0 4px 16px rgba(82, 196, 26, 0.15)',
                overflow: 'hidden',
                position: 'relative',
              }}
              bodyStyle={{ padding: '20px' }}
            >
              <div style={{ position: 'absolute', top: 0, right: 0, width: '100px', height: '100px', background: 'radial-gradient(circle, rgba(82, 196, 26, 0.2) 0%, transparent 70%)', borderRadius: '50%', transform: 'translate(30%, -30%)', pointerEvents: 'none' }} />
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', position: 'relative', zIndex: 1 }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <Text style={{ fontSize: '12px', fontWeight: 500, color: '#262626', display: 'block', marginBottom: '8px' }}>Total Facilities</Text>
                  <Text style={{ fontSize: '26px', fontWeight: 600, color: '#389e0d', display: 'block', marginBottom: '4px' }}>{aggregates.total_facilities || 0}</Text>
                  <div style={{ height: '1px', background: 'rgba(82, 196, 26, 0.3)', marginTop: '8px' }} />
                </div>
                <div style={{ width: '48px', height: '48px', borderRadius: '12px', background: 'rgba(82, 196, 26, 0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '24px', color: '#52c41a', flexShrink: 0 }}>
                  <BankOutlined />
                </div>
              </div>
            </Card>
          </Col>
          <Col xs={24} sm={12} md={8} lg={6}>
            <Card
              style={{
                height: '100%',
                background: 'linear-gradient(135deg, #f9f0ff 0%, #efdbff 100%)',
                border: '2px solid #722ed1',
                borderRadius: '16px',
                boxShadow: '0 4px 16px rgba(114, 46, 209, 0.15)',
                overflow: 'hidden',
                position: 'relative',
              }}
              bodyStyle={{ padding: '20px' }}
            >
              <div style={{ position: 'absolute', top: 0, right: 0, width: '100px', height: '100px', background: 'radial-gradient(circle, rgba(114, 46, 209, 0.2) 0%, transparent 70%)', borderRadius: '50%', transform: 'translate(30%, -30%)', pointerEvents: 'none' }} />
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', position: 'relative', zIndex: 1 }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <Text style={{ fontSize: '12px', fontWeight: 500, color: '#262626', display: 'block', marginBottom: '8px' }}>Total Claims</Text>
                  <Text style={{ fontSize: '26px', fontWeight: 600, color: '#531dab', display: 'block', marginBottom: '4px' }}>{aggregates.total_claims || 0}</Text>
                  <div style={{ height: '1px', background: 'rgba(114, 46, 209, 0.3)', marginTop: '8px' }} />
                </div>
                <div style={{ width: '48px', height: '48px', borderRadius: '12px', background: 'rgba(114, 46, 209, 0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '24px', color: '#722ed1', flexShrink: 0 }}>
                  <FileTextOutlined />
                </div>
              </div>
            </Card>
          </Col>
          <Col xs={24} sm={12} md={8} lg={6}>
            <Card
              style={{
                height: '100%',
                background: 'linear-gradient(135deg, #fff7e6 0%, #ffe7ba 100%)',
                border: '2px solid #fa8c16',
                borderRadius: '16px',
                boxShadow: '0 4px 16px rgba(250, 140, 22, 0.15)',
                overflow: 'hidden',
                position: 'relative',
              }}
              bodyStyle={{ padding: '20px' }}
            >
              <div style={{ position: 'absolute', top: 0, right: 0, width: '100px', height: '100px', background: 'radial-gradient(circle, rgba(250, 140, 22, 0.2) 0%, transparent 70%)', borderRadius: '50%', transform: 'translate(30%, -30%)', pointerEvents: 'none' }} />
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', position: 'relative', zIndex: 1 }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <Text style={{ fontSize: '12px', fontWeight: 500, color: '#262626', display: 'block', marginBottom: '8px' }}>Claims approved</Text>
                  <Text style={{ fontSize: '24px', fontWeight: 600, color: '#d46b08', display: 'block', marginBottom: '4px' }}>
                    <CurrencyFormat
                      value={getClaimsApprovedTotal(aggregates)}
                      displayType={'text'}
                      thousandSeparator={true}
                      prefix={'₦'}
                      decimalScale={2}
                    />
                  </Text>
                  <div style={{ height: '1px', background: 'rgba(250, 140, 22, 0.3)', marginTop: '8px' }} />
                </div>
                <div style={{ width: '48px', height: '48px', borderRadius: '12px', background: 'rgba(250, 140, 22, 0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '24px', color: '#fa8c16', flexShrink: 0 }}>
                  <DollarOutlined />
                </div>
              </div>
            </Card>
          </Col>
          <Col xs={24} sm={12} md={8} lg={6}>
            <Card
              style={{
                height: '100%',
                background: 'linear-gradient(135deg, #f0f5ff 0%, #d6e4ff 100%)',
                border: '2px solid #2f54eb',
                borderRadius: '16px',
                boxShadow: '0 4px 16px rgba(47, 84, 235, 0.15)',
                overflow: 'hidden',
                position: 'relative',
              }}
              bodyStyle={{ padding: '20px' }}
            >
              <div style={{ position: 'absolute', top: 0, right: 0, width: '100px', height: '100px', background: 'radial-gradient(circle, rgba(47, 84, 235, 0.2) 0%, transparent 70%)', borderRadius: '50%', transform: 'translate(30%, -30%)', pointerEvents: 'none' }} />
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', position: 'relative', zIndex: 1 }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <Text style={{ fontSize: '12px', fontWeight: 500, color: '#262626', display: 'block', marginBottom: '8px' }}>Submitted to PA</Text>
                  <Text style={{ fontSize: '24px', fontWeight: 600, color: '#1d39c4', display: 'block', marginBottom: '4px' }}>
                    <CurrencyFormat
                      value={getSubmittedTotal(aggregates)}
                      displayType={'text'}
                      thousandSeparator={true}
                      prefix={'₦'}
                      decimalScale={2}
                    />
                  </Text>
                  <div style={{ height: '1px', background: 'rgba(47, 84, 235, 0.3)', marginTop: '8px' }} />
                </div>
                <div style={{ width: '48px', height: '48px', borderRadius: '12px', background: 'rgba(47, 84, 235, 0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '24px', color: '#2f54eb', flexShrink: 0 }}>
                  <DollarOutlined />
                </div>
              </div>
            </Card>
          </Col>
          <Col xs={24} sm={12} md={8} lg={6}>
            <Card
              style={{
                height: '100%',
                background: 'linear-gradient(135deg, #f9f0ff 0%, #efdbff 100%)',
                border: '2px solid #722ed1',
                borderRadius: '16px',
                boxShadow: '0 4px 16px rgba(114, 46, 209, 0.15)',
                overflow: 'hidden',
                position: 'relative',
              }}
              bodyStyle={{ padding: '20px' }}
            >
              <div style={{ position: 'absolute', top: 0, right: 0, width: '100px', height: '100px', background: 'radial-gradient(circle, rgba(114, 46, 209, 0.2) 0%, transparent 70%)', borderRadius: '50%', transform: 'translate(30%, -30%)', pointerEvents: 'none' }} />
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', position: 'relative', zIndex: 1 }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <Text style={{ fontSize: '12px', fontWeight: 500, color: '#262626', display: 'block', marginBottom: '8px' }}>PA approved</Text>
                  <Text style={{ fontSize: '24px', fontWeight: 600, color: '#531dab', display: 'block', marginBottom: '4px' }}>
                    <CurrencyFormat
                      value={getPaApprovedTotal(aggregates)}
                      displayType={'text'}
                      thousandSeparator={true}
                      prefix={'₦'}
                      decimalScale={2}
                    />
                  </Text>
                  <div style={{ height: '1px', background: 'rgba(114, 46, 209, 0.3)', marginTop: '8px' }} />
                </div>
                <div style={{ width: '48px', height: '48px', borderRadius: '12px', background: 'rgba(114, 46, 209, 0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '24px', color: '#722ed1', flexShrink: 0 }}>
                  <DollarOutlined />
                </div>
              </div>
            </Card>
          </Col>
          {aggregates.claims_with_queries !== undefined && (
            <Col xs={24} sm={12} md={8} lg={6}>
              <Card
                style={{
                  height: '100%',
                  background: 'linear-gradient(135deg, #fff0f6 0%, #ffd6e7 100%)',
                  border: '2px solid #eb2f96',
                  borderRadius: '16px',
                  boxShadow: '0 4px 16px rgba(235, 47, 150, 0.15)',
                  overflow: 'hidden',
                  position: 'relative',
                }}
                bodyStyle={{ padding: '20px' }}
              >
                <div style={{ position: 'absolute', top: 0, right: 0, width: '100px', height: '100px', background: 'radial-gradient(circle, rgba(235, 47, 150, 0.2) 0%, transparent 70%)', borderRadius: '50%', transform: 'translate(30%, -30%)', pointerEvents: 'none' }} />
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', position: 'relative', zIndex: 1 }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <Text style={{ fontSize: '12px', fontWeight: 500, color: '#262626', display: 'block', marginBottom: '8px' }}>Claims with Queries</Text>
                    <Text style={{ fontSize: '26px', fontWeight: 600, color: '#c41d7f', display: 'block', marginBottom: '4px' }}>{aggregates.claims_with_queries || 0}</Text>
                    <div style={{ height: '1px', background: 'rgba(235, 47, 150, 0.3)', marginTop: '8px' }} />
                  </div>
                  <div style={{ width: '48px', height: '48px', borderRadius: '12px', background: 'rgba(235, 47, 150, 0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '24px', color: '#eb2f96', flexShrink: 0 }}>
                    <QuestionCircleOutlined />
                  </div>
                </div>
              </Card>
            </Col>
          )}
        </Row>
      )}

      {/* Year Averages Trend (if available) */}
      {aggregates?.year_averages && aggregates.year_averages.length > 0 && (
        <div style={{ marginBottom: '24px' }}>
          <Title level={4} style={{ marginBottom: '16px' }}>
            <LineChartOutlined style={{ marginRight: '8px' }} />
            Year Averages
          </Title>
          <Row gutter={[16, 16]}>
            {aggregates.year_averages.map((yearData, index) => {
              const colors = [
                { bg: 'linear-gradient(135deg, #e6f7ff 0%, #bae7ff 100%)', border: '#1890ff', text: '#0050b3', icon: '#1890ff' },
                { bg: 'linear-gradient(135deg, #f6ffed 0%, #d9f7be 100%)', border: '#52c41a', text: '#389e0d', icon: '#52c41a' },
                { bg: 'linear-gradient(135deg, #fff7e6 0%, #ffe7ba 100%)', border: '#fa8c16', text: '#d46b08', icon: '#fa8c16' },
                { bg: 'linear-gradient(135deg, #f9f0ff 0%, #efdbff 100%)', border: '#722ed1', text: '#531dab', icon: '#722ed1' },
                { bg: 'linear-gradient(135deg, #fff0f6 0%, #ffd6e7 100%)', border: '#eb2f96', text: '#c41d7f', icon: '#eb2f96' },
              ];
              const colorScheme = colors[index % colors.length];
              
              return (
                <Col xs={24} sm={12} md={8} lg={6} key={yearData.year}>
                  <Card
                    style={{
                      height: '100%',
                      background: colorScheme.bg,
                      border: `2px solid ${colorScheme.border}`,
                      borderRadius: '16px',
                      boxShadow: `0 4px 16px ${colorScheme.border}15`,
                      overflow: 'hidden',
                      position: 'relative',
                    }}
                    bodyStyle={{ padding: '20px' }}
                  >
                    <div style={{ position: 'absolute', top: 0, right: 0, width: '100px', height: '100px', background: `radial-gradient(circle, ${colorScheme.border}22 0%, transparent 70%)`, borderRadius: '50%', transform: 'translate(30%, -30%)', pointerEvents: 'none' }} />
                    <div style={{ position: 'relative', zIndex: 1 }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                        <Text strong style={{ fontSize: '20px', color: colorScheme.text }}>
                          {yearData.year}
                        </Text>
                        <div style={{ width: '40px', height: '40px', borderRadius: '10px', background: `${colorScheme.icon}15`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '20px', color: colorScheme.icon }}>
                          <LineChartOutlined />
                        </div>
                      </div>
                      <div style={{ marginBottom: '8px' }}>
                        <Text type="secondary" style={{ fontSize: '11px', display: 'block', marginBottom: '4px' }}>Claims</Text>
                        <Text strong style={{ fontSize: '18px', color: colorScheme.text }}>{yearData.claims_count?.toLocaleString() || 0}</Text>
                      </div>
                      <div style={{ marginBottom: '8px' }}>
                        <Text type="secondary" style={{ fontSize: '11px', display: 'block', marginBottom: '4px' }}>Submitted to PA</Text>
                        <CurrencyFormat
                          value={getSubmittedTotal(yearData)}
                          displayType={'text'}
                          thousandSeparator={true}
                          prefix={'₦'}
                          decimalScale={0}
                          renderText={(value) => <Text strong style={{ fontSize: '14px', color: colorScheme.text }}>{value}</Text>}
                        />
                      </div>
                      <div style={{ marginBottom: '8px' }}>
                        <Text type="secondary" style={{ fontSize: '11px', display: 'block', marginBottom: '4px' }}>PA approved</Text>
                        <CurrencyFormat
                          value={getPaApprovedTotal(yearData)}
                          displayType={'text'}
                          thousandSeparator={true}
                          prefix={'₦'}
                          decimalScale={0}
                          renderText={(value) => <Text strong style={{ fontSize: '14px', color: colorScheme.text }}>{value}</Text>}
                        />
                      </div>
                      <div style={{ marginBottom: '8px' }}>
                        <Text type="secondary" style={{ fontSize: '11px', display: 'block', marginBottom: '4px' }}>Claims approved</Text>
                        <CurrencyFormat
                          value={getClaimsApprovedTotal(yearData)}
                          displayType={'text'}
                          thousandSeparator={true}
                          prefix={'₦'}
                          decimalScale={0}
                          renderText={(value) => <Text strong style={{ fontSize: '16px', color: colorScheme.text }}>{value}</Text>}
                        />
                      </div>
                      <div style={{ paddingTop: '8px', borderTop: `1px solid ${colorScheme.border}33` }}>
                        <Text type="secondary" style={{ fontSize: '11px', display: 'block', marginBottom: '4px' }}>Avg/Claim</Text>
                        <CurrencyFormat
                          value={yearData.average_claim_cost || 0}
                          displayType={'text'}
                          thousandSeparator={true}
                          prefix={'₦'}
                          decimalScale={2}
                          renderText={(value) => <Text strong style={{ fontSize: '16px', color: colorScheme.text }}>{value}</Text>}
                        />
                      </div>
                    </div>
                  </Card>
                </Col>
              );
            })}
          </Row>
        </div>
      )}

      {/* Visual Separator */}
      <div style={{ 
        marginBottom: '24px', 
        paddingTop: '24px',
        borderTop: '2px solid #e8e8e8',
        position: 'relative'
      }}>
        <Title level={4} style={{ margin: 0, marginBottom: '16px', color: '#262626' }}>
          Months
        </Title>
      </div>
      
      <Row gutter={[16, 16]}>
        {months.map((month) => (
          <Col xs={24} sm={12} md={8} lg={6} key={month.month || month.month_key}>
            <Card
              className="month-card"
              hoverable
              onClick={() => onSelect && onSelect(month)}
              style={{
                borderRadius: '8px',
                cursor: 'pointer',
                transition: 'all 0.2s',
              }}
            >
              <div className="month-card__content">
                <div className="month-card__header" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  {canArchiveClaims ? (
                    <Checkbox
                      checked={selectedMonths.includes(monthKeyOf(month))}
                      onClick={(e) => e.stopPropagation()}
                      onChange={(e) => toggleMonth(monthKeyOf(month), e.target.checked)}
                      aria-label={`Select ${month.month_label || formatMonthLabel(monthKeyOf(month))}`}
                    />
                  ) : null}
                  <CalendarOutlined style={{ marginRight: '8px', color: '#1890ff', fontSize: '20px' }} />
                  <Title level={4} style={{ margin: 0, flex: 1 }}>
                    {month.month_label || formatMonthLabel(month.month || month.month_key)}
                  </Title>
                  {canArchiveClaims ? (
                    <Button
                      size="small"
                      type="text"
                      danger={!archivedView}
                      icon={<InboxOutlined />}
                      onClick={(e) => { e.stopPropagation(); openArchiveFor(monthKeyOf(month)); }}
                    >
                      {archivedView ? 'Restore' : 'Archive'}
                    </Button>
                  ) : null}
                </div>
                
                <div className="month-card__stats">
                  <Space direction="vertical" size="small" style={{ width: '100%' }}>
                    <div>
                      <BankOutlined style={{ marginRight: '4px' }} />
                      <Tag color="blue">{month.facilities_count || 0}</Tag>
                      <Text type="secondary" style={{ fontSize: '12px' }}>
                        {month.facilities_count === 1 ? 'facility' : 'facilities'}
                      </Text>
                    </div>
                    <div>
                      <FileTextOutlined style={{ marginRight: '4px' }} />
                      <Tag color="green">{month.total_claims || 0}</Tag>
                      <Text type="secondary" style={{ fontSize: '12px' }}>
                        {month.total_claims === 1 ? 'claim' : 'claims'}
                      </Text>
                    </div>
                    <div>
                      <DollarOutlined style={{ marginRight: '4px' }} />
                      <Space direction="vertical" size={0} style={{ display: 'inline-flex', verticalAlign: 'top' }}>
                        <Text type="secondary" style={{ fontSize: '11px' }}>
                          Submitted{' '}
                          <CurrencyFormat
                            value={getSubmittedTotal(month)}
                            displayType={'text'}
                            thousandSeparator={true}
                            prefix={'₦'}
                            decimalScale={0}
                          />
                        </Text>
                        <Text type="secondary" style={{ fontSize: '11px' }}>
                          PA approved{' '}
                          <CurrencyFormat
                            value={getPaApprovedTotal(month)}
                            displayType={'text'}
                            thousandSeparator={true}
                            prefix={'₦'}
                            decimalScale={0}
                          />
                        </Text>
                        <Text style={{ fontSize: '12px', fontWeight: 600 }}>
                          Claims approved{' '}
                          <CurrencyFormat
                            value={getClaimsApprovedTotal(month)}
                            displayType={'text'}
                            thousandSeparator={true}
                            prefix={'₦'}
                            decimalScale={0}
                          />
                        </Text>
                      </Space>
                    </div>
                  </Space>
                </div>

                <div className="month-card__action">
                  <RightOutlined />
                </div>
              </div>
            </Card>
          </Col>
        ))}
      </Row>

      {pagination.total > pagination.pageSize && (
        <div className="admin-claims-months-list__pagination">
          <Pagination
            current={pagination.current}
            pageSize={pagination.pageSize}
            total={pagination.total}
            onChange={handlePageChange}
            showSizeChanger={false}
            showQuickJumper
            showTotal={(total, range) =>
              `${range[0]}-${range[1]} of ${total} months`
            }
          />
        </div>
      )}
      {canArchiveClaims ? (
        <ArchiveClaimsModal
          open={!!archiveRequest}
          mode={archivedView ? 'unarchive' : 'archive'}
          request={archiveRequest}
          allowMonthWide
          segment={null}
          onClose={() => setArchiveRequest(null)}
          onSuccess={() => {
            setArchiveRequest(null);
            setSelectedMonths([]);
            fetchMonths(pagination.current || 1, searchQuery);
          }}
        />
      ) : null}
    </div>
  );
};

export default AdminClaimsMonthsList;

